import { forwardRef, useId, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/shared/lib/utils'
import { Spinner } from './Spinner'

type Variant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'brand'
  | 'success'
  | 'danger'
  | 'danger-ghost'
type Size = 'xs' | 'sm' | 'md' | 'lg' | 'icon'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
  /**
   * Why the button is disabled (F-043). With a reason the button stays
   * focusable (aria-disabled instead of the disabled attribute), ignores
   * clicks, shows the reason as a tooltip and exposes it to screen readers via
   * aria-describedby, so a keyboard user is not left with a dead control.
   */
  disabledReason?: string
}

const variants: Record<Variant, string> = {
  primary: 'bg-brand-orange text-white hover:bg-brand-orange-dark active:scale-[0.98] shadow-sm',
  secondary: 'bg-white text-brand-rail border border-gray-200 hover:bg-gray-50 active:scale-[0.98]',
  outline:
    'bg-transparent text-brand-orange border border-brand-orange hover:bg-brand-orange/5 active:scale-[0.98]',
  ghost: 'text-brand-rail hover:bg-gray-100 active:scale-[0.98]',
  brand: 'text-brand-orange hover:bg-brand-orange/10 active:scale-[0.98]',
  success:
    'bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98] shadow-sm focus-visible:ring-emerald-500',
  danger:
    'bg-red-600 text-white hover:bg-red-700 active:scale-[0.98] shadow-sm focus-visible:ring-red-500',
  'danger-ghost':
    'text-gray-500 hover:text-red-600 hover:bg-red-50 active:scale-[0.98] focus-visible:ring-red-500',
}

// Buttons keep their desktop height on touch screens; the 44px tap area is an
// invisible hit zone added by the shell's .touch-targets rules (index.css).
const sizes: Record<Size, string> = {
  xs: 'h-6 px-2 text-xs',
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'h-8 w-8 p-0',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      className,
      disabled,
      disabledReason,
      children,
      onClick,
      title,
      ...props
    },
    ref,
  ) => {
    const reasonId = useId()
    // Explained-disabled: focusable, announced, inert to clicks and submits.
    const explained = !!disabled && !!disabledReason && !loading
    const describedBy = explained
      ? [props['aria-describedby'], reasonId].filter(Boolean).join(' ')
      : props['aria-describedby']
    return (
      <>
        <button
          ref={ref}
          disabled={explained ? undefined : disabled || loading}
          className={cn(
            'inline-flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap',
            'transition-all duration-150 focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2',
            'disabled:pointer-events-none disabled:opacity-50',
            'aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:active:scale-100',
            variants[variant],
            sizes[size],
            fullWidth && 'w-full',
            className,
          )}
          {...props}
          aria-disabled={explained ? true : props['aria-disabled']}
          aria-describedby={describedBy}
          title={explained ? (title ?? disabledReason) : title}
          onClick={(e) => {
            if (explained) {
              e.preventDefault()
              return
            }
            onClick?.(e)
          }}
        >
          {loading && <Spinner size="sm" className="text-current" />}
          {children}
        </button>
        {explained && (
          <span id={reasonId} className="sr-only">
            {disabledReason}
          </span>
        )}
      </>
    )
  },
)
Button.displayName = 'Button'
